import React, { useEffect, useState, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";

import Input from "shared/components/FormElements/Input";
import Select from "shared/components/FormElements/Select";
import DatePicker from "shared/components/FormElements/DatePicker";
import NotFound from "shared/components/UIElements/NotFound";
import FormFooter from "shared/components/FormElements/FormFooter";
import DeleteModal from "components/shared/DeleteModal";
import ItemGenerated from "components/shared/ItemGenerated";
import Button from "shared/components/FormElements/Button";

import { VALIDATOR_REQUIRE, VALIDATOR_NONE } from "shared/util/validators";

import { PERMISSION_TYPES } from "@routebot/constants";

import { parseComplexDate } from "shared/util/util";

import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import { StoreContext } from "shared/context/store-context";
import { AuthContext } from "shared/context/auth-context";

const InventoryAccidentItem = ({ itemId, onClose, isNewItem, gotoItem, DeleteItem, UpdateItem, InsertItem }) => {
  // standard code
  const { t } = useTranslation();

  const [isItemLoaded, setIsItemLoaded] = useState(false);
  const [loadedItem, setLoadedItem] = useState();
  const [generatedItemId, setGeneratedItemId] = useState();
  const { isLoading, error, sendRequest } = useHttpClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // context
  const { accidents, driverOptions, vehicleOptions } = useContext(StoreContext);
  const auth = useContext(AuthContext);

  // item info
  const itemType = "accident";
  const itemTextType = "accident";
  const endPointUpdate = "vehicle-accidents";
  const endPointDelete = "vehicle-accidents";
  const endPointCreate = "vehicle-accidents";

  // Accident type options
  const accidentTypes = [
    { value: "çarpışma", label: t("inventoryAccident.accidentTypes.collision") },
    { value: "devrilme", label: t("inventoryAccident.accidentTypes.rollover") },
    { value: "yanma", label: t("inventoryAccident.accidentTypes.fire") },
    { value: "suya düşme", label: t("inventoryAccident.accidentTypes.water") },
    { value: "diğer", label: t("inventoryAccident.accidentTypes.other") },
  ];

  // Severity options
  const severityOptions = [
    { value: "hafif", label: t("inventoryAccident.severity.light") },
    { value: "orta", label: t("inventoryAccident.severity.medium") },
    { value: "ağır", label: t("inventoryAccident.severity.heavy") },
    { value: "çok ağır", label: t("inventoryAccident.severity.veryHeavy") },
  ];

  // Vehicle status options
  const vehicleStatusOptions = [
    { value: "kullanılabilir", label: t("inventoryAccident.vehicleStatus.usable") },
    { value: "tamir ediliyor", label: t("inventoryAccident.vehicleStatus.underRepair") },
    { value: "hurda", label: t("inventoryAccident.vehicleStatus.scrap") },
    { value: "satıldı", label: t("inventoryAccident.vehicleStatus.sold") },
  ];

  // form states
  const [formState, inputHandler] = useForm(
    {
      vehicle: { value: "", isValid: false },
      accidentDate: { value: "", isValid: false },
      accidentLocation: { value: "", isValid: true },
      accidentType: { value: "çarpışma", isValid: true },
      severity: { value: "orta", isValid: true },
      damageDescription: { value: "", isValid: true },
      actualDamageCost: { value: "", isValid: true },
      companyPayment: { value: "", isValid: true },
      driverPayment: { value: "", isValid: true },
      insuranceCompany: { value: "", isValid: true },
      insuranceClaimNumber: { value: "", isValid: true },
      insuranceAmount: { value: "", isValid: true },
      policeReportNumber: { value: "", isValid: true },
      driver: { value: "", isValid: false },
      passengersInjured: { value: "0", isValid: true },
      passengersDeceased: { value: "0", isValid: true },
      vehicleStatus: { value: "kullanılabilir", isValid: true },
      repairStartDate: { value: "", isValid: true },
      repairEndDate: { value: "", isValid: true },
      repairWorkshop: { value: "", isValid: true },
      notes: { value: "", isValid: true },
      counterpartyName: { value: "", isValid: true },
      counterpartyPhone: { value: "", isValid: true },
      counterpartyAddress: { value: "", isValid: true },
    },
    false
  );

  // form states fetch
  const fetchItem = useCallback(async () => {
    setIsItemLoaded(false);
    try {
      const currentAccident = accidents.find((accident) => accident.id === itemId);
      setLoadedItem(currentAccident);
    } catch (err) {
      console.log(err);
    }
    setIsItemLoaded(true);
  }, [accidents, itemId]);

  // standard code
  useEffect(() => {
    fetchItem();
  }, [itemId, fetchItem]);

  // item update
  const itemUpdateSubmitHandler = async (event) => {
    event.preventDefault();

    try {
      const infoToBeSent = {
        vehicle: formState.inputs.vehicle.value,
        accidentDate: formState.inputs.accidentDate.value,
        accidentLocation: formState.inputs.accidentLocation.value,
        accidentType: formState.inputs.accidentType.value,
        severity: formState.inputs.severity.value,
        damageDescription: formState.inputs.damageDescription.value,
        actualDamageCost: formState.inputs.actualDamageCost.value
          ? parseFloat(formState.inputs.actualDamageCost.value)
          : null,
        companyPayment: formState.inputs.companyPayment.value
          ? parseFloat(formState.inputs.companyPayment.value)
          : null,
        driverPayment: formState.inputs.driverPayment.value ? parseFloat(formState.inputs.driverPayment.value) : null,
        insuranceCompany: formState.inputs.insuranceCompany.value,
        insuranceClaimNumber: formState.inputs.insuranceClaimNumber.value,
        insuranceAmount: formState.inputs.insuranceAmount.value
          ? parseFloat(formState.inputs.insuranceAmount.value)
          : null,
        policeReportNumber: formState.inputs.policeReportNumber.value,
        driver: formState.inputs.driver.value,
        passengersInjured: parseInt(formState.inputs.passengersInjured.value),
        passengersDeceased: parseInt(formState.inputs.passengersDeceased.value),
        vehicleStatus: formState.inputs.vehicleStatus.value,
        repairStartDate: formState.inputs.repairStartDate.value,
        repairEndDate: formState.inputs.repairEndDate.value,
        repairWorkshop: formState.inputs.repairWorkshop.value,
        notes: formState.inputs.notes.value,
        counterpartyName: formState.inputs.counterpartyName.value,
        counterpartyPhone: formState.inputs.counterpartyPhone.value,
        counterpartyAddress: formState.inputs.counterpartyAddress.value,
      };

      const endpoint = isNewItem
        ? `${process.env.REACT_APP_BACKEND_URL}/${endPointCreate}`
        : `${process.env.REACT_APP_BACKEND_URL}/${endPointUpdate}/${itemId}`;

      const postType = isNewItem ? "POST" : "PATCH";

      const responseData = await sendRequest(endpoint, postType, JSON.stringify(infoToBeSent), {
        "Content-Type": "application/json",
        Authorization: "Bearer " + auth.token,
      });

      if (responseData?.[itemType]) {
        if (isNewItem) {
          InsertItem(responseData[itemType]);
          setGeneratedItemId(responseData[itemType].id);
        } else {
          UpdateItem(responseData[itemType]);
          window.toast.success(t("updateSuccess"));
        }
      }
    } catch (err) {
      console.log(err);
      if (err?.code) {
        window.toast.error(t("httpCodes." + err.code));
      } else {
        window.toast.error(t("actionFailed"));
      }
    }
  };

  // standard code
  if (isItemLoaded && !isNewItem && !loadedItem && !error) {
    return <NotFound item={itemTextType}></NotFound>;
  }

  if (generatedItemId) {
    return (
      <ItemGenerated
        itemType={itemTextType}
        onGotoNewItem={() => gotoItem(generatedItemId)}
        onCreateNewItem={() => setGeneratedItemId(null)}
        onClose={() => {
          setGeneratedItemId(false);
          onClose();
        }}
      />
    );
  }

  return (
    <React.Fragment>
      <DeleteModal
        endPoint={endPointDelete}
        itemType={itemTextType}
        itemId={itemId}
        onDeleted={() => {
          DeleteItem(itemId);
          onClose();
        }}
        onClose={() => setShowConfirmModal(false)}
        showModal={showConfirmModal}
      />

      {isItemLoaded && (
        <form className="form-container">
          <div className="horizontal-flex">
            <Select
              id="vehicle"
              label={t("inventoryAccident.fields.vehicleRequired")}
              options={vehicleOptions}
              validators={[VALIDATOR_REQUIRE()]}
              onInput={inputHandler}
              initialValue={vehicleOptions.find((vehicle) => vehicle.value === loadedItem?.vehicle) || null}
              initialValid={!isNewItem}
              disabled={!isNewItem}
            />

            <Select
              id="driver"
              label={t("inventoryAccident.fields.driverRequired")}
              options={driverOptions}
              validators={[VALIDATOR_REQUIRE()]}
              onInput={inputHandler}
              initialValue={driverOptions.find((driver) => driver.value === loadedItem?.driver) || null}
              initialValid={!isNewItem}
            />

            <DatePicker
              id="accidentDate"
              label={t("inventoryAccident.fields.accidentDateRequired")}
              onInput={inputHandler}
              initialValue={loadedItem?.accidentDate || parseComplexDate(new Date())}
              initialValid={!isNewItem}
            />
          </div>

          {!isNewItem && loadedItem.googleDriveFolderId && (
            <div style={{ marginBottom: "1rem" }}>
              <div className="horizontal-flex" style={{ alignItems: "center", justifyContent: "flex-start" }}>
                <div>
                  <Button
                    type="button"
                    href={`https://drive.google.com/drive/folders/${loadedItem?.googleDriveFolderId}`}
                  >
                    {t("inventoryCommon.goToFiles")}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="horizontal-flex">
            <Input
              id="accidentLocation"
              element="input"
              type="text"
              label={t("inventoryAccident.fields.accidentLocation")}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={loadedItem?.accidentLocation || ""}
              initialValid={true}
            />
          </div>

          <div className="horizontal-flex">
            <Select
              id="vehicleStatus"
              label={t("inventoryAccident.fields.vehicleStatus")}
              options={vehicleStatusOptions}
              onInput={inputHandler}
              initialValue={
                vehicleStatusOptions.find((status) => status.value === loadedItem?.vehicleStatus) ||
                vehicleStatusOptions[0]
              }
              initialValid={true}
            />
            <Select
              id="accidentType"
              label={t("inventoryAccident.fields.accidentType")}
              options={accidentTypes}
              onInput={inputHandler}
              initialValue={accidentTypes.find((type) => type.value === loadedItem?.accidentType) || accidentTypes[0]}
              initialValid={true}
            />

            <Select
              id="severity"
              label={t("inventoryAccident.fields.severity")}
              options={severityOptions}
              onInput={inputHandler}
              initialValue={
                severityOptions.find((severity) => severity.value === loadedItem?.severity) || severityOptions[0]
              }
              initialValid={true}
            />
          </div>

          <Input
            id="damageDescription"
            element="textarea"
            label={t("inventoryAccident.fields.damageDescription")}
            validators={[VALIDATOR_NONE()]}
            onInput={inputHandler}
            rows={3}
            initialValue={loadedItem?.damageDescription || ""}
            initialValid={true}
          />

          <div className="horizontal-flex">
            <Input
              id="actualDamageCost"
              element="input"
              type="number"
              label={t("inventoryAccident.fields.actualDamageCost")}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={loadedItem?.actualDamageCost || "0"}
              initialValid={true}
            />

            <Input
              id="companyPayment"
              element="input"
              type="number"
              label={t("inventoryAccident.fields.companyPayment")}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={loadedItem?.companyPayment || "0"}
              initialValid={true}
            />

            <Input
              id="insuranceAmount"
              element="input"
              type="number"
              label={t("inventoryAccident.fields.insuranceAmount")}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={loadedItem?.insuranceAmount || "0"}
              initialValid={true}
            />

            <Input
              id="driverPayment"
              element="input"
              type="number"
              label={t("inventoryAccident.fields.driverPayment")}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={loadedItem?.driverPayment || "0"}
              initialValid={true}
            />
          </div>

          <div className="horizontal-flex">
            <Input
              id="insuranceCompany"
              element="input"
              type="text"
              label={t("inventoryAccident.fields.insuranceCompany")}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={loadedItem?.insuranceCompany || ""}
              initialValid={true}
            />

            <Input
              id="insuranceClaimNumber"
              element="input"
              type="text"
              label={t("inventoryAccident.fields.insuranceClaimNumber")}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={loadedItem?.insuranceClaimNumber || ""}
              initialValid={true}
            />

            <Input
              id="policeReportNumber"
              element="input"
              type="text"
              label={t("inventoryAccident.fields.policeReportNumber")}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={loadedItem?.policeReportNumber || ""}
              initialValid={true}
            />
          </div>

          <div className="horizontal-flex">
            <Input
              id="counterpartyName"
              element="input"
              type="text"
              label={t("inventoryAccident.fields.counterpartyName")}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={loadedItem?.counterpartyName || ""}
              initialValid={true}
            />

            <Input
              id="counterpartyPhone"
              element="input"
              type="text"
              label={t("inventoryAccident.fields.counterpartyPhone")}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={loadedItem?.counterpartyPhone || ""}
              initialValid={true}
            />

            <Input
              id="counterpartyAddress"
              element="input"
              type="text"
              label={t("inventoryAccident.fields.counterpartyAddress")}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={loadedItem?.counterpartyAddress || ""}
              initialValid={true}
            />
          </div>

          {/* <div className="horizontal-flex">
            <Input
              id="passengersInjured"
              element="input"
              type="number"
              label="Yaralı Yolcu Sayısı"
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={loadedItem?.passengersInjured || "0"}
              initialValid={true}
            />

            <Input
              id="passengersDeceased"
              element="input"
              type="number"
              label="Ölen Yolcu Sayısı"
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={loadedItem?.passengersDeceased || "0"}
              initialValid={true}
            />
          </div> */}

          <div className="horizontal-flex">
            <DatePicker
              id="repairStartDate"
              label={t("inventoryAccident.fields.repairStartDate")}
              onInput={inputHandler}
              initialValue={loadedItem?.repairStartDate || ""}
              initialValid={true}
            />

            <DatePicker
              id="repairEndDate"
              label={t("inventoryAccident.fields.repairEndDate")}
              onInput={inputHandler}
              initialValue={loadedItem?.repairEndDate || ""}
              initialValid={true}
            />

            <Input
              id="repairWorkshop"
              element="input"
              type="text"
              label={t("inventoryAccident.fields.repairWorkshop")}
              validators={[VALIDATOR_NONE()]}
              onInput={inputHandler}
              initialValue={loadedItem?.repairWorkshop || ""}
              initialValid={true}
            />
          </div>

          <Input
            id="notes"
            element="textarea"
            label={t("inventoryAccident.fields.notes")}
            validators={[VALIDATOR_NONE()]}
            onInput={inputHandler}
            rows={7}
            initialValue={loadedItem?.notes || ""}
            initialValid={true}
          />

          {auth.permissions[PERMISSION_TYPES.ACCESS_TO_WRITE_INVENTORY] && (
            <FormFooter
              isNewItem={isNewItem}
              onUpdate={itemUpdateSubmitHandler}
              onDelete={() => setShowConfirmModal(true)}
              isUpdating={isLoading}
              disabled={
                !formState.isValid ||
                isLoading ||
                !formState.inputs.accidentDate.value ||
                !formState.inputs.vehicle.value ||
                !formState.inputs.driver.value
              }
            />
          )}
        </form>
      )}
    </React.Fragment>
  );
};

export default InventoryAccidentItem;
